import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { ChartCard } from '@/shared/components/dashboard/ChartCard';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { barOption, donutOption } from '@/shared/components/dashboard/chartOptions';
import { ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef, FilterDef } from '@/shared/components/ui/DataTable';
import { Icons } from '@/shared/components/ui/icons';
import { Button } from '@/shared/components/ui/button';
import { useProducts } from '../hooks/useProducts';
import { useActivateProduct } from '../hooks/useActivateProduct';
import { useDeleteProduct } from '../hooks/useDeleteProduct';
import { ProductStatusBadge } from '../components/ProductStatusBadge';
import type { ProductResponseDTO } from '../types';

const C = {
  teal: '#14b8a6',
  blue: '#3b82f6',
  emerald: '#10b981',
  amber: '#f59e0b',
  rose: '#f43f5e',
  violet: '#8b5cf6',
  cyan: '#06b6d4',
  orange: '#f97316',
  indigo: '#6366f1',
};

export function ProductsListPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error } = useProducts({});

  const toggleActive = useActivateProduct();
  const deleteProduct = useDeleteProduct();

  const isAr = i18n.language === 'ar';
  const canCreate = user?.permissions.includes('products.create');
  const products = useMemo(() => data?.items ?? [], [data?.items]);
  const activeCount = products.filter((p) => p.is_active).length;
  const inactiveCount = Math.max(products.length - activeCount, 0);
  const namedCount = products.filter((p) => p.name_ar || p.name_en).length;

  const statusOption = useMemo(
    () =>
      donutOption(t('products.isActive'), [
        { name: t('products.status.active'), value: activeCount },
        { name: t('products.status.inactive'), value: inactiveCount },
      ]),
    [activeCount, inactiveCount, t],
  );

  const namesOption = useMemo(
    () =>
      barOption(
        t('products.title'),
        products.slice(0, 8).map((p) => p.slug),
        products.slice(0, 8).map((p) => (p.is_active ? 1 : 0)),
        true,
      ),
    [products, t],
  );

  const treemapOption = useMemo(() => {
    const items = products.map((p) => ({
      name: p.slug,
      value: (p.name_en ?? p.slug).length + (p.name_ar ?? '').length + 2,
      itemStyle: { color: p.is_active ? C.teal : C.rose, borderColor: '#fff', borderWidth: 2 },
    }));
    return {
      tooltip: { formatter: '{b}' },
      series: [
        {
          type: 'treemap',
          data:
            items.length > 0
              ? items
              : [
                  {
                    name: t('products.charts.noProducts'),
                    value: 1,
                    itemStyle: { color: '#e5e7eb' },
                  },
                ],
          width: '100%',
          height: '100%',
          label: { show: true, fontSize: 12, color: '#fff', fontWeight: 600 },
          breadcrumb: { show: false },
          levels: [{ itemStyle: { borderWidth: 3, borderColor: '#fff', gapWidth: 3 } }],
        },
      ],
    };
  }, [products, t]);

  const effectScatterOption = useMemo(() => {
    const activeData = products
      .filter((p) => p.is_active)
      .map((p) => [p.slug.length, (p.name_en ?? '').length]);
    const inactiveData = products
      .filter((p) => !p.is_active)
      .map((p) => [p.slug.length, (p.name_en ?? '').length]);
    const ax = {
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#9ca3af', fontSize: 10 },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
    };
    return {
      tooltip: {
        trigger: 'item',
        formatter: (p: { data: number[] }) =>
          t('products.charts.tooltipSlugName', { slug: p.data[0], name: p.data[1] }),
      },
      legend: { bottom: 0, textStyle: { color: '#6b7280', fontSize: 10 } },
      grid: { left: 40, right: 16, top: 16, bottom: 44 },
      xAxis: {
        type: 'value',
        name: t('products.charts.slugLength'),
        nameTextStyle: { color: '#9ca3af', fontSize: 10 },
        ...ax,
      },
      yAxis: {
        type: 'value',
        name: t('products.charts.nameLength'),
        nameTextStyle: { color: '#9ca3af', fontSize: 10 },
        ...ax,
      },
      series: [
        {
          name: t('products.status.active'),
          type: 'effectScatter',
          data: activeData.length > 0 ? activeData : [[0, 0]],
          symbolSize: 12,
          showEffectOn: 'render',
          rippleEffect: { period: 4, scale: 3, brushType: 'stroke' },
          itemStyle: { color: C.teal },
        },
        {
          name: t('products.status.inactive'),
          type: 'scatter',
          data: inactiveData.length > 0 ? inactiveData : [[0, 0]],
          symbolSize: 8,
          itemStyle: { color: C.rose, opacity: 0.5 },
        },
      ],
    };
  }, [products, t]);

  const columns: ColumnDef<ProductResponseDTO>[] = useMemo(
    () => [
      {
        key: 'name',
        header: isAr ? t('products.nameAr') : t('products.name'),
        accessor: (p) => {
          const description = isAr
            ? (p.description_ar ?? p.description_en)
            : (p.description_en ?? p.description_ar);
          return (
            <button
              type="button"
              className="flex items-center gap-3 text-start"
              onClick={() =>
                void navigate({ to: '/dashboard/products/$productId', params: { productId: p.id } })
              }
            >
              {p.logo_url ? (
                <img
                  src={p.logo_url}
                  alt=""
                  className="h-8 w-8 rounded-lg object-cover ring-1 ring-border"
                />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icons.Package className="h-4 w-4" />
                </span>
              )}
              <div className="min-w-0">
                <p className="font-medium text-primary hover:underline leading-tight">
                  {isAr ? p.name_ar : p.name_en}
                </p>
                {description && (
                  <p className="truncate text-xs text-muted-foreground max-w-[200px]">
                    {description}
                  </p>
                )}
              </div>
            </button>
          );
        },
        sortValue: (p) => [p.name_en ?? '', p.name_ar ?? '', p.slug].join(' '),
      },
      {
        key: 'slug',
        header: t('products.slug'),
        accessor: (p) => <span className="font-mono text-xs text-muted-foreground">{p.slug}</span>,
        sortValue: (p) => p.slug,
        align: 'center',
      },
      {
        key: 'status',
        header: t('products.isActive'),
        accessor: (p) => <ProductStatusBadge isActive={p.is_active} />,
        sortValue: (p) => (p.is_active ? 'active' : 'inactive'),
        align: 'center',
      },
      {
        key: 'actions',
        header: '',
        accessor: (p) => (
          <div className="flex items-center justify-end gap-1">
            <button
              title={t('common.view')}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              onClick={() =>
                void navigate({ to: '/dashboard/products/$productId', params: { productId: p.id } })
              }
            >
              <Icons.Eye className="h-3.5 w-3.5" />
            </button>
            <button
              title={
                p.is_active ? t('products.actions.deactivate') : t('products.actions.activate')
              }
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              disabled={toggleActive.isPending}
              onClick={() => toggleActive.mutate({ id: p.id, activate: !p.is_active })}
            >
              {p.is_active ? (
                <Icons.Ban className="h-3.5 w-3.5" />
              ) : (
                <Icons.CheckCircle className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              title={t('products.actions.delete')}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              disabled={deleteProduct.isPending}
              onClick={() => {
                if (confirm(t('products.actions.delete'))) deleteProduct.mutate(p.id);
              }}
            >
              <Icons.Trash className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
        align: 'right',
      },
    ],
    [isAr, t, navigate, toggleActive, deleteProduct],
  );

  const filters = useMemo<FilterDef[]>(
    () => [
      {
        key: 'name',
        label: t('common.search'),
        type: 'text',
        placeholder: t('products.searchPlaceholder'),
      },
      {
        key: 'status',
        label: t('common.status'),
        type: 'select',
        options: [
          { label: t('products.status.active'), value: 'active' },
          { label: t('products.status.inactive'), value: 'inactive' },
        ],
      },
    ],
    [t],
  );

  if (isLoading) return <LoadingPanel />;
  if (error) return <ErrorPanel message={t('common.error')} />;

  return (
    <div className="w-full min-w-0 space-y-6 animate-fade-in">
      <PageHero
        title={t('products.title')}
        subtitle={t('dashboard.modules.products')}
        icon={<Icons.Package className="h-7 w-7" />}
        eyebrow={t('products.dashboard.catalog')}
        metrics={[
          { label: t('products.title'), value: String(data?.total ?? products.length) },
          { label: t('products.status.active'), value: String(activeCount) },
          { label: t('products.status.inactive'), value: String(inactiveCount) },
        ]}
        actions={
          canCreate ? (
            <Button onClick={() => void navigate({ to: '/dashboard/products/new' })}>
              <Icons.Plus className="me-1.5 h-4 w-4" />
              {t('products.create')}
            </Button>
          ) : null
        }
      />

      <KpiGrid>
        <KpiCard
          title={t('products.title')}
          value={String(data?.total ?? products.length)}
          icon={<Icons.Package className="h-4 w-4" />}
          tone="blue"
          hint={t('products.metrics.totalInCatalog')}
        />
        <KpiCard
          title={t('products.status.active')}
          value={String(activeCount)}
          icon={<Icons.CheckCircle className="h-4 w-4" />}
          tone="green"
        />
        <KpiCard
          title={t('products.status.inactive')}
          value={String(inactiveCount)}
          icon={<Icons.Ban className="h-4 w-4" />}
          tone="amber"
        />
        <KpiCard
          title={t('products.metrics.named')}
          value={String(namedCount)}
          icon={<Icons.Tag className="h-4 w-4" />}
          tone="violet"
          hint={t('products.metrics.productsWithNames')}
        />
      </KpiGrid>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <ChartCard
          title={t('products.isActive')}
          subtitle={t('products.charts.activeVsInactive')}
          option={statusOption}
        />
        <ChartCard
          title={t('products.charts.catalogMap')}
          subtitle={t('products.charts.catalogMapSubtitle')}
          option={treemapOption}
          height={260}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <ChartCard
          title={t('products.title')}
          subtitle={t('products.charts.bySlug')}
          option={namesOption}
        />
        <ChartCard
          title={t('products.charts.activityScatter')}
          subtitle={t('products.charts.activityScatterSubtitle')}
          option={effectScatterOption}
          height={260}
        />
      </div>

      <DataTable
        data={products}
        columns={columns}
        filters={filters}
        rowKey={(p) => p.id}
        selectable
        total={data?.total ?? products.length}
        initialPageSize={10}
        emptyMessage={t('products.notFound')}
      />
    </div>
  );
}
